// Iter186b — Portfolio Optimization (MPT / Monte Carlo) modal.
// On-demand simulation with loading state; efficient-frontier scatter with
// max-Sharpe / max-Sortino portfolios vs the user's actual current weights.
// Projection/informational tool only — never a rebalancing instruction.
import React from "react";
import api from "@/lib/api";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const SERIES = [
  { key: "current", label: "Current portfolio", color: "#F59E0B" },
  { key: "max_sharpe", label: "Max Sharpe", color: "#10B981" },
  { key: "max_sortino", label: "Max Sortino", color: "#60A5FA" },
];

function MetricsRow({ label, p, color }) {
  if (!p) return null;
  return (
    <tr className="border-b" style={{ borderColor: "var(--hoip-divider)" }} data-testid={`mpt-metrics-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <td className="py-1 pr-2 font-bold whitespace-nowrap" style={{ color }}>{label}</td>
      <td className="py-1 px-2 font-mono-data text-right">{p.return_pct}%</td>
      <td className="py-1 px-2 font-mono-data text-right">{p.volatility_pct}%</td>
      <td className="py-1 px-2 font-mono-data text-right">{p.sharpe ?? "—"}</td>
      <td className="py-1 px-2 font-mono-data text-right">{p.sortino ?? "—"}</td>
    </tr>
  );
}

export function PortfolioOptimizationModal({ open, onClose }) {
  const [lookback, setLookback] = React.useState("2y");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async (lb, force = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await api.get(`/portfolio/mpt-simulation?lookback=${lb}${force ? "&force=true" : ""}`);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Simulation failed.");
      setData(null);
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { if (open) load(lookback); }, [open, lookback, load]);

  if (!open) return null;
  const tickers = data?.tickers || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="mpt-modal">
      <div className="w-full max-w-4xl max-h-[88vh] overflow-y-auto rounded-md border shadow-lg"
        style={{ background: "var(--hoip-bg)", borderColor: "var(--hoip-border)" }}>
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--hoip-border)" }}>
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-bold" style={{ color: "var(--hoip-text-primary)" }}>
              Portfolio Optimization — Simulated Outcome Ranges
            </h2>
            <div className="inline-flex border rounded-sm overflow-hidden" style={{ borderColor: "var(--hoip-border)" }}>
              {["2y", "5y"].map(lb => (
                <button key={lb} onClick={() => setLookback(lb)}
                  className={`px-2.5 h-6 text-[10px] font-mono-data font-bold border-r last:border-r-0 ${lookback === lb ? "bg-[#1A1A1A] text-emerald-400" : "text-zinc-500 hover:text-zinc-300"}`}
                  style={{ borderColor: "var(--hoip-border)" }}
                  data-testid={`mpt-lookback-${lb}`}>
                  {lb} lookback
                </button>
              ))}
            </div>
            {data?.from_cache && (
              <button onClick={() => load(lookback, true)} className="text-[9px] px-1.5 py-0.5 rounded-sm border hover:bg-[var(--hoip-hover)]"
                style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
                title="Cached result — click to re-run the simulation" data-testid="mpt-recompute-btn">
                cached · re-run
              </button>
            )}
          </div>
          <button onClick={onClose} className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="mpt-close-btn">✕</button>
        </div>

        <div className="mx-4 mt-3 px-3 py-2 border rounded-sm text-[10px] leading-relaxed"
          style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-nav)", color: "var(--hoip-text-tertiary)" }}
          data-testid="mpt-disclaimer">
          <span className="font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Simulated projection based on historical returns — not a forecast or recommendation.</span>{" "}
          Monte Carlo over random weight combinations of your current stock holdings using historical daily returns.
          Past performance does not determine future results; this tool does not instruct you to rebalance.
          Model outputs depend on the assumptions and data inputs shown below. Historical and simulated data may be
          incomplete or subject to error. Simulated results are not guarantees of future performance.
        </div>

        {loading && (
          <div className="p-10 text-center text-sm" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="mpt-loading">
            Running 10,000-portfolio Monte Carlo over the {lookback} window…
            <span className="block mt-1 text-[10px]">Fetches daily history for each holding — typically 3-8s.</span>
          </div>
        )}
        {error && <div className="p-6 text-center text-xs text-red-400" data-testid="mpt-error">{error}</div>}

        {!loading && data && (
          <>
            <div className="px-4 pt-3 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
              {data.n_simulations.toLocaleString()} simulations · {data.history_days} trading days ({data.start_date} → {data.end_date}) ·
              risk-free {data.risk_free_pct}%
              {data.excluded?.length > 0 && (
                <span className="text-amber-500"> · excluded (insufficient history): {data.excluded.join(", ")}</span>
              )}
            </div>
            <div className="px-2 py-2" data-testid="mpt-frontier-chart">
              <ResponsiveContainer width="100%" height={280}>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 28, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                  <XAxis type="number" dataKey="v" name="Volatility" unit="%" domain={["auto", "auto"]}
                    tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} stroke="var(--hoip-text-tertiary)"
                    label={{ value: "Annualized volatility %", position: "insideBottom", offset: -5, fontSize: 9, fill: "var(--hoip-text-tertiary)" }} />
                  <YAxis type="number" dataKey="r" name="Return" unit="%" domain={["auto", "auto"]}
                    tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} stroke="var(--hoip-text-tertiary)"
                    label={{ value: "Annualized return %", angle: -90, position: "insideLeft", fontSize: 9, fill: "var(--hoip-text-tertiary)" }} />
                  <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px" }}
                    formatter={(val, name) => [`${val}%`, name === "v" ? "Volatility" : name === "r" ? "Return" : name]} />
                  <Legend verticalAlign="top" wrapperStyle={{ fontSize: 10 }} />
                  <Scatter name="Simulated portfolios" data={data.frontier} fill="#8B5CF6" opacity={0.25} shape="circle" legendType="circle" isAnimationActive={false} />
                  {SERIES.map(({ key, label, color }) => data[key] && (
                    <Scatter key={key} name={label}
                      data={[{ v: data[key].volatility_pct, r: data[key].return_pct }]}
                      fill={color} shape="diamond" legendType="diamond" isAnimationActive={false} />
                  ))}
                </ScatterChart>
              </ResponsiveContainer>
            </div>

            <div className="px-4 pb-2 overflow-x-auto">
              <table className="w-full text-[10px]" data-testid="mpt-metrics-table">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}>
                    <th className="text-left py-1 text-[9px] uppercase">Portfolio</th>
                    <th className="text-right px-2 text-[9px] uppercase">Return</th>
                    <th className="text-right px-2 text-[9px] uppercase">Volatility</th>
                    <th className="text-right px-2 text-[9px] uppercase">Sharpe</th>
                    <th className="text-right px-2 text-[9px] uppercase">Sortino</th>
                  </tr>
                </thead>
                <tbody style={{ color: "var(--hoip-text-secondary)" }}>
                  {SERIES.map(({ key, label, color }) => <MetricsRow key={key} label={label} p={data[key]} color={color} />)}
                </tbody>
              </table>
            </div>

            <div className="px-4 pb-4 overflow-x-auto">
              <div className="text-[9px] font-bold uppercase tracking-wide mb-1" style={{ color: "var(--hoip-text-tertiary)" }}>
                Simulated weight combinations vs current allocation
              </div>
              <table className="w-full text-[10px]" data-testid="mpt-weights-table">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}>
                    <th className="text-left py-1 text-[9px] uppercase">Ticker</th>
                    {SERIES.map(({ key, label, color }) => (
                      <th key={key} className="text-right px-2 text-[9px] uppercase" style={{ color }}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>
                  {tickers.map(t => (
                    <tr key={t} className="border-b" style={{ borderColor: "var(--hoip-divider)" }} data-testid={`mpt-weight-row-${t}`}>
                      <td className="py-1 font-bold" style={{ color: "var(--hoip-text-primary)" }}>{t}</td>
                      {SERIES.map(({ key }) => (
                        <td key={key} className="text-right px-2">{data[key]?.weights?.[t] != null ? `${data[key].weights[t]}%` : "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[9px] mt-2 leading-relaxed" style={{ color: "var(--hoip-text-tertiary)" }}>
                The max-Sharpe and max-Sortino columns are the best-scoring random weight combinations found in this
                simulation — historical artifacts, not target allocations. Any change to your portfolio is entirely your decision.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
